import * as React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Timer, Search, Bell, CalendarDays } from 'lucide-react';
import { useCookingStore } from '@/stores/cooking-store';
import { UserAvatar } from '@/components/layout/UserAvatar';
import { GlobalSearchDialog } from '@/components/ui/GlobalSearchDialog';
import { NotificationsPanel, useNotificationCount } from '@/components/layout/NotificationsPanel';

export function Header() {
  const location = useLocation();
  const { t } = useTranslation(['pages', 'common']);
  const { session, getActiveTimers } = useCookingStore();

  const activeTimers = getActiveTimers();
  const hasActiveTimers = activeTimers.length > 0;

  const [searchOpen, setSearchOpen] = React.useState(false);
  const [notifOpen, setNotifOpen] = React.useState(false);
  const notifCount = useNotificationCount();

  // Cmd/Ctrl + K opens global search
  React.useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && (event.key === 'k' || event.key === 'K')) {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const pageTitles: Record<string, string> = {
    '/': t('pages:header.home'),
    '/planner': t('pages:header.mealPlanner'),
    '/pantry': t('pages:header.pantry'),
    '/cookbook': t('pages:header.myCookbook'),
    '/cook': t('pages:header.letsCook'),
    '/discover': t('pages:header.discover'),
    '/settings': t('pages:header.settings'),
  };

  const getPageTitle = () => {
    if (location.pathname === '/cookbook/new') return t('pages:header.addRecipe');
    if (location.pathname.endsWith('/edit')) return t('pages:header.editRecipe');
    if (location.pathname.startsWith('/cookbook/')) return t('pages:header.recipeDetails');
    if (location.pathname.startsWith('/cook/session')) return session?.recipeName ?? t('pages:header.cookingSession');
    return pageTitles[location.pathname] ?? t('common:app.name');
  };

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        height: '64px',
        padding: '0 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid var(--border-primary)',
        transition: 'backdrop-filter var(--motion-duration-standard) var(--motion-ease-standard), background-color var(--motion-duration-standard) var(--motion-ease-standard)',
      }}
    >
      {/* Left side */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Mobile logo */}
        <NavLink
          to="/"
          style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}
          className="lg:hidden"
        >
          <img
            src="/logo.png"
            alt={t('common:app.name')}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: 'var(--radius-md)',
              objectFit: 'contain',
            }}
          />
          <span style={{ fontWeight: 700, color: 'var(--text-primary)' }} className="hidden sm:block">
            {t('common:app.name')}
          </span>
        </NavLink>
        
        {/* Desktop page title */}
        <h1 
          style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)', margin: 0, animation: 'fadeInDown var(--motion-duration-standard) var(--motion-ease-emphasis)' }}
          className="hidden lg:block"
        >
          {getPageTitle()}
        </h1>
      </div>

      {/* Right side */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {hasActiveTimers && (
          <NavLink
            to={session ? '/cook/session' : '/cook'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px',
              borderRadius: 'var(--radius-full)',
              background: 'var(--accent-success-bg)',
              color: 'var(--accent-success-text)',
              fontSize: '14px',
              fontWeight: 600,
              textDecoration: 'none',
              transition: 'transform var(--motion-duration-standard) var(--motion-ease-emphasis), box-shadow var(--motion-duration-standard) var(--motion-ease-standard)',
              boxShadow: 'none',
            }}
          >
            <Timer style={{ width: '16px', height: '16px' }} />
            <span className="hidden sm:inline">{t('pages:header.timers', { count: activeTimers.length })}</span>
            <span className="sm:hidden">{activeTimers.length}</span>
          </NavLink>
        )}

        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          className="ui-icon-btn"
          style={{
            width: '40px',
            height: '40px',
            borderRadius: 'var(--radius-md)',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-tertiary)',
            transition: 'color var(--motion-duration-fast) var(--motion-ease-standard), background-color var(--motion-duration-fast) var(--motion-ease-standard), transform var(--motion-duration-fast) var(--motion-ease-emphasis)',
          }}
          aria-label={t('common:a11y.searchRecipes')}
          onMouseEnter={(event) => {
            event.currentTarget.style.background = 'var(--border-secondary)';
            event.currentTarget.style.color = 'var(--accent-primary-hover)';
            event.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.background = 'transparent';
            event.currentTarget.style.color = 'var(--text-tertiary)';
            event.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <Search style={{ width: '20px', height: '20px' }} />
        </button>

        <button
          type="button"
          onClick={() => setNotifOpen(true)}
          className="ui-icon-btn"
          style={{
            width: '40px',
            height: '40px',
            borderRadius: 'var(--radius-md)',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-tertiary)',
            transition: 'color var(--motion-duration-fast) var(--motion-ease-standard), background-color var(--motion-duration-fast) var(--motion-ease-standard), transform var(--motion-duration-fast) var(--motion-ease-emphasis)',
            position: 'relative',
          }}
          aria-label={t('common:a11y.notifications')}
          onMouseEnter={(event) => {
            event.currentTarget.style.background = 'var(--border-secondary)';
            event.currentTarget.style.color = 'var(--accent-primary-hover)';
            event.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.background = 'transparent';
            event.currentTarget.style.color = 'var(--text-tertiary)';
            event.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <Bell style={{ width: '20px', height: '20px' }} />
          {notifCount > 0 && (
            <span
              aria-hidden
              style={{
                position: 'absolute',
                top: '6px',
                insetInlineEnd: '6px',
                minWidth: '16px',
                height: '16px',
                padding: '0 4px',
                borderRadius: 'var(--radius-full)',
                background: 'var(--accent-error)',
                color: 'var(--text-inverse)',
                fontSize: '10px',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
                boxShadow: '0 0 0 2px var(--bg-primary)',
              }}
            >
              {notifCount > 9 ? '9+' : notifCount}
            </span>
          )}
        </button>

        <NavLink
          to="/planner"
          className="lg:hidden"
          style={{
            height: '36px',
            borderRadius: 'var(--radius-full)',
            background: 'linear-gradient(180deg, #ECFEFF 0%, #CFFAFE 100%)',
            border: '1px solid #A5F3FC',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '0 12px',
            color: 'var(--accent-primary-active)',
            textDecoration: 'none',
            fontSize: '13px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            transition: 'color var(--motion-duration-fast) var(--motion-ease-standard), background var(--motion-duration-fast) var(--motion-ease-standard), border-color var(--motion-duration-fast) var(--motion-ease-standard), transform var(--motion-duration-fast) var(--motion-ease-emphasis), box-shadow var(--motion-duration-fast) var(--motion-ease-standard)',
          }}
          aria-label={t('common:nav.planner')}
          onMouseEnter={(event) => {
            event.currentTarget.style.background = 'linear-gradient(180deg, #CFFAFE 0%, #A5F3FC 100%)';
            event.currentTarget.style.borderColor = '#67E8F9';
            event.currentTarget.style.transform = 'translateY(-1px)';
            event.currentTarget.style.boxShadow = '0 4px 12px rgba(8, 145, 178, 0.15)';
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.background = 'linear-gradient(180deg, #ECFEFF 0%, #CFFAFE 100%)';
            event.currentTarget.style.borderColor = '#A5F3FC';
            event.currentTarget.style.transform = 'translateY(0)';
            event.currentTarget.style.boxShadow = 'none';
          }}
        >
          <CalendarDays style={{ width: '16px', height: '16px' }} />
          <span>{t('common:nav.plan')}</span>
        </NavLink>

        <UserAvatar />
      </div>

      <GlobalSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      <NotificationsPanel open={notifOpen} onOpenChange={setNotifOpen} />
    </header>
  );
}
